import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildAssetUrl, employeeAPI, onboardingAPI, payrollAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { Camera, Upload, Save, Trash2, Plus, FileText, Edit, Clock3, Send, ShieldCheck, Contact, Briefcase, Wallet, GitPullRequest, History } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { TableExportActions } from './ui/table-export-actions';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const ProfileSection = ({ employeeId }) => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  
  // New state
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [jobHistory, setJobHistory] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({ name: '', relationship: '', phone: '', email: '', address: '', is_primary: false });
  
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [newJob, setNewJob] = useState({ company_name: '', designation: '', start_date: '', end_date: '', is_current_company: false, location: '', description: '' });

  const [showDocDialog, setShowDocDialog] = useState(false);
  const [newDoc, setNewDoc] = useState({ type_id: '', file: null, notes: '', expiry_date: '' });
  const [docChecklist, setDocChecklist] = useState(null);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [profileChangeRequests, setProfileChangeRequests] = useState([]);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [changeDraft, setChangeDraft] = useState({ section: 'personal', field_name: '', requested_value: '', reason: '' });
  const [activeProfileTab, setActiveProfileTab] = useState('personal');

  const isAdminView = !!employeeId;

  const fetchDocumentChecklist = useCallback(async () => {
    try {
      const targetId = isAdminView ? employeeId : profile?.employee_id;
      if (!targetId) return;
      const response = await onboardingAPI.getDocumentChecklistProgress(targetId);
      setDocChecklist(response.data);
    } catch (error) {
      console.error("Error fetching document checklist progress", error);
    }
  }, [employeeId, isAdminView, profile]);

  const fetchDocumentExpiryAlerts = useCallback(async () => {
    try {
      if (!isAdminView) return;
      const response = await onboardingAPI.getDocumentExpiryAlerts(30);
      const alerts = Array.isArray(response.data) ? response.data : [];
      setExpiryAlerts(alerts.filter((row) => row.employee_id === employeeId));
    } catch (error) {
      console.error("Error fetching document expiry alerts", error);
    }
  }, [employeeId, isAdminView]);

  const emergencyContactExportColumns = useMemo(
    () => [
      { label: 'Name', accessor: (row) => row.name },
      { label: 'Relationship', accessor: (row) => row.relationship },
      { label: 'Phone', accessor: (row) => row.phone },
      { label: 'Email', accessor: (row) => row.email },
      { label: 'Primary', accessor: (row) => (row.is_primary ? 'Yes' : 'No') },
    ],
    []
  );

  const salaryHistoryExportColumns = useMemo(
    () => [
      { label: 'Effective From', accessor: (row) => row.effective_from },
      { label: 'Effective To', accessor: (row) => row.effective_to },
      { label: 'Annual CTC', accessor: (row) => row.ctc_annual },
      { label: 'Net Salary', accessor: (row) => row.net_salary },
    ],
    []
  );

  const profileRequestExportColumns = useMemo(
    () => [
      { label: 'Field', accessor: (row) => row.field_name },
      { label: 'Current Value', accessor: (row) => row.current_value },
      { label: 'Requested Value', accessor: (row) => row.requested_value },
      { label: 'Status', accessor: (row) => row.status },
      { label: 'Requested On', accessor: (row) => row.created_at },
    ],
    []
  );

  useEffect(() => {
    fetchProfile();
    fetchEmergencyContacts();
    fetchJobHistory();
    fetchDocuments();
    fetchDocumentTypes();
    fetchSalaryHistory();
    if (!isAdminView) {
      fetchProfileChangeRequests();
    }
    // Profile subresources are intentionally reloaded when the viewed employee changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const fetchSalaryHistory = async () => {
    try {
        const targetId = isAdminView ? employeeId : (profile?.employee_id);
        if (!targetId) return;
        
        // Note: Currently salary history API is only for admin/manager or self
        // but frontend needs logic to know current user's ID if not passed as prop
        // We'll rely on the API call to handle permissions or return empty
        
        // However, we need the employee ID for the API call
        // If profile is not yet loaded, we might miss this.
        // Let's rely on the effect dependency, but we need profile to be loaded first for non-admin view?
        // Actually employeeAPI.getMyProfile returns ID.
        // Let's wait until profile is loaded if !isAdminView
    } catch (error) {
        console.error("Error fetching salary history", error);
    }
  };
  
  // Better effect for salary history
  useEffect(() => {
      if (profile && (isAdminView || profile.employee_id)) {
          const fetchSal = async () => {
              try {
                  const targetId = isAdminView ? employeeId : profile.employee_id;
                  const response = await payrollAPI.getSalaryHistory(targetId);
                  setSalaryHistory(response.data);
              } catch (error) {
                  console.error("Error fetching salary history", error);
              }
          };
          fetchSal();
          fetchDocumentChecklist();
          fetchDocumentExpiryAlerts();
      }
  }, [profile, isAdminView, employeeId, fetchDocumentChecklist, fetchDocumentExpiryAlerts]);

  const fetchProfile = async () => {
    try {
      const response = isAdminView 
        ? await employeeAPI.get(employeeId)
        : await employeeAPI.getMyProfile();
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching profile", error);
    }
  };
  
  const fetchDocumentTypes = async () => {
    try {
      const response = await onboardingAPI.getDocumentTypes();
      setDocumentTypes(response.data);
    } catch (error) {
      console.error("Error fetching document types", error);
    }
  };
  
  const fetchEmergencyContacts = async () => {
    try {
      const response = isAdminView
        ? await employeeAPI.getEmployeeEmergencyContacts(employeeId)
        : await employeeAPI.getEmergencyContacts();
      setEmergencyContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts", error);
    }
  };
  
  const fetchJobHistory = async () => {
    try {
      const response = isAdminView
        ? await employeeAPI.getEmployeeJobHistory(employeeId)
        : await employeeAPI.getJobHistory();
      setJobHistory(response.data);
    } catch (error) {
      console.error("Error fetching job history", error);
    }
  };
  
  const fetchDocuments = async () => {
    try {
      const response = isAdminView
        ? await onboardingAPI.getEmployeeDocuments(employeeId)
        : await employeeAPI.getMyDocuments();
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents", error);
    }
  };

  const fetchProfileChangeRequests = async () => {
    try {
      if (isAdminView) return;
      const response = await employeeAPI.getMyProfileChangeRequests();
      setProfileChangeRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching profile change requests", error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      if (isAdminView) {
        await employeeAPI.update(employeeId, {
          phone: formData.phone,
          address: formData.address,
          // Add other fields admin might want to update
          department: formData.department,
          designation: formData.designation,
          reporting_manager_id: formData.reporting_manager_id,
          location: formData.location,
          employment_status: formData.employment_status
        });
      } else {
        await employeeAPI.updateMyProfile({
          phone: formData.phone,
          address: formData.address
        });
      }
      toast.success("Profile updated successfully");
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append('file', file);

    setUploading(true);
    try {
      if (isAdminView) {
          await employeeAPI.uploadEmployeePhoto(employeeId, data);
      } else {
          await employeeAPI.uploadProfilePhoto(data);
      }
      toast.success("Profile photo updated");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };
  
  const handleAddContact = async () => {
    try {
      if (editingContact) {
          // Update
          if (isAdminView) {
              await employeeAPI.updateEmployeeEmergencyContact(employeeId, editingContact.id, newContact);
          } else {
              await employeeAPI.updateEmergencyContact(editingContact.id, newContact);
          }
          toast.success("Contact updated");
      } else {
          // Create
          if (isAdminView) {
            await employeeAPI.addEmployeeEmergencyContact(employeeId, newContact);
          } else {
            await employeeAPI.addEmergencyContact(newContact);
          }
          toast.success("Contact added");
      }
      setShowContactDialog(false);
      setEditingContact(null);
      setNewContact({ name: '', relationship: '', phone: '', email: '', address: '', is_primary: false });
      fetchEmergencyContacts();
    } catch (error) {
      toast.error(editingContact ? "Failed to update contact" : "Failed to add contact");
    }
  };
  
  const handleEditContact = (contact) => {
      setEditingContact(contact);
      setNewContact({
          name: contact.name,
          relationship: contact.relationship,
          phone: contact.phone,
          email: contact.email || '',
          address: contact.address || '',
          is_primary: contact.is_primary
      });
      setShowContactDialog(true);
  };
  
  const handleDeleteContact = async (id) => {
    try {
      if (isAdminView) {
        await employeeAPI.deleteEmployeeEmergencyContact(employeeId, id);
      } else {
        await employeeAPI.deleteEmergencyContact(id);
      }
      toast.success("Contact deleted");
      fetchEmergencyContacts();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };
  
  const handleAddJob = async () => {
    try {
      if (editingJob) {
          // Update
          if (isAdminView) {
              await employeeAPI.updateEmployeeJobHistory(employeeId, editingJob.id, newJob);
          } else {
              await employeeAPI.updateJobHistory(editingJob.id, newJob);
          }
          toast.success("Job history updated");
      } else {
          // Create
          if (isAdminView) {
            await employeeAPI.addEmployeeJobHistory(employeeId, newJob);
          } else {
            await employeeAPI.addJobHistory(newJob);
          }
          toast.success("Job history added");
      }
      setShowJobDialog(false);
      setEditingJob(null);
      setNewJob({ company_name: '', designation: '', start_date: '', end_date: '', is_current_company: false, location: '', description: '' });
      fetchJobHistory();
    } catch (error) {
      toast.error(editingJob ? "Failed to update job history" : "Failed to add job history");
    }
  };

  const handleEditJob = (job) => {
      setEditingJob(job);
      setNewJob({
          company_name: job.company_name,
          designation: job.designation,
          start_date: job.start_date,
          end_date: job.end_date || '',
          is_current_company: job.is_current_company,
          location: job.location || '',
          description: job.description || ''
      });
      setShowJobDialog(true);
  };

  const handleDeleteJob = async (id) => {
    try {
        if (isAdminView) {
            await employeeAPI.deleteEmployeeJobHistory(employeeId, id);
        } else {
            // Employee cannot delete their own job history usually, but if allowed:
            // await employeeAPI.deleteJobHistory(id); 
            // Note: deleteJobHistory is not in api.js or backend routes_employee.py for 'me'
            // Let's assume only admin can delete or check if we added it.
            // Looking at api.js: deleteJobHistory is NOT there for 'me'.
            // Looking at routes_employee.py: delete_job_history is NOT there for 'me'.
            toast.error("Only Admin can delete job history");
            return;
        }
        toast.success("Job history deleted");
        fetchJobHistory();
    } catch (error) {
        toast.error("Failed to delete job history");
    }
  };

  const handleUploadDocument = async () => {
    if (!newDoc.file || !newDoc.type_id) {
      toast.error("Please select a file and document type");
      return;
    }

    const formData = new FormData();
    formData.append('file', newDoc.file);
    
    try {
      const targetId = isAdminView ? employeeId : profile.employee_id;
      await onboardingAPI.uploadDocument(targetId, formData, {
        document_type_id: newDoc.type_id,
        notes: newDoc.notes,
        expiry_date: newDoc.expiry_date || undefined
      });
      toast.success("Document uploaded successfully");
      setShowDocDialog(false);
      setNewDoc({ type_id: '', file: null, notes: '', expiry_date: '' });
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to upload document");
    }
  };

  const openChangeRequestDialog = (fieldName, section = 'personal') => {
    setChangeDraft({
      section,
      field_name: fieldName,
      requested_value: '',
      reason: '',
    });
    setShowChangeDialog(true);
  };

  const handleSubmitChangeRequest = async () => {
    if (!changeDraft.field_name || !changeDraft.requested_value) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await employeeAPI.createProfileChangeRequest(changeDraft);
      toast.success('Change request submitted for approval');
      setShowChangeDialog(false);
      setChangeDraft({ section: 'personal', field_name: '', requested_value: '', reason: '' });
      fetchProfileChangeRequests();
    } catch (error) {
      toast.error('Failed to submit change request');
    }
  };

  const profileStats = [
    { label: 'Emergency Contacts', value: emergencyContacts.length, tab: 'emergency', icon: Contact, tone: 'primary' },
    { label: 'Job Records', value: jobHistory.length, tab: 'job', icon: Briefcase, tone: 'info' },
    { label: 'Documents', value: documents.length, tab: 'documents', icon: FileText, tone: 'success' },
    { label: 'Compensation Records', value: salaryHistory.length, tab: 'compensation', icon: Wallet, tone: 'purple' },
  ];

  const completenessChecks = useMemo(() => ([
    Boolean(profile?.first_name),
    Boolean(profile?.last_name),
    Boolean(profile?.email),
    Boolean(profile?.phone),
    Boolean(profile?.address),
    Boolean(profile?.department),
    Boolean(profile?.designation),
    Boolean(profile?.location),
    Boolean(profile?.date_of_birth),
    Boolean(profile?.reporting_manager_id),
    emergencyContacts.length > 0,
    documents.length > 0,
  ]), [profile, emergencyContacts.length, documents.length]);

  const completenessScore = useMemo(() => {
    const completed = completenessChecks.filter(Boolean).length;
    return Math.round((completed / completenessChecks.length) * 100);
  }, [completenessChecks]);

  const getRequestBadgeVariant = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'approved') return 'success';
    if (normalized === 'rejected') return 'danger';
    if (normalized === 'pending') return 'warning';
    return 'outline';
  };

  const timelineEvents = useMemo(() => {
    const events = [];

    if (profile?.date_of_joining) {
      events.push({
        id: `joining-${profile?.employee_id}`,
        timestamp: new Date(profile.date_of_joining).getTime(),
        title: 'Joined Organization',
        detail: `Started as ${profile?.designation || 'Employee'}`,
        type: 'milestone',
      });
    }

    emergencyContacts.forEach((contact) => {
      if (!contact?.created_at) return;
      events.push({
        id: `emergency-${contact.id}`,
        timestamp: new Date(contact.created_at).getTime(),
        title: 'Emergency Contact Added',
        detail: `${contact.name} (${contact.relationship})`,
        type: 'emergency',
      });
    });

    jobHistory.forEach((job) => {
      if (!job?.start_date) return;
      events.push({
        id: `job-${job.id}`,
        timestamp: new Date(job.start_date).getTime(),
        title: 'Job History Recorded',
        detail: `${job.designation} at ${job.company_name}`,
        type: 'job',
      });
    });

    documents.forEach((doc) => {
      if (!doc?.uploaded_at) return;
      events.push({
        id: `doc-${doc.id}`,
        timestamp: new Date(doc.uploaded_at).getTime(),
        title: 'Document Uploaded',
        detail: doc.document_name || doc.document_type?.name || 'Document',
        type: 'document',
      });
    });

    salaryHistory.forEach((salary) => {
      if (!salary?.effective_from) return;
      events.push({
        id: `salary-${salary.id}`,
        timestamp: new Date(salary.effective_from).getTime(),
        title: 'Compensation Updated',
        detail: `CTC ${salary.ctc_annual?.toLocaleString?.() || salary.ctc_annual || ''}`,
        type: 'salary',
      });
    });

    profileChangeRequests.forEach((request) => {
      if (!request?.created_at) return;
      events.push({
        id: `request-${request.id}`,
        timestamp: new Date(request.created_at).getTime(),
        title: `Change Request: ${request.field_name}`,
        detail: `Status: ${request.status}`,
        type: 'request',
      });
    });

    return events
      .filter((event) => !Number.isNaN(event.timestamp))
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [profile, emergencyContacts, jobHistory, documents, salaryHistory, profileChangeRequests]);

  if (!profile) return <div>Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
           <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                <AvatarImage src={buildAssetUrl(profile.profile_photo_url)} />
                <AvatarFallback className="text-2xl">{profile.first_name[0]}</AvatarFallback>
              </Avatar>
              <label className="absolute bottom-0 right-0 bg-primary text-primary-foreground p-1.5 rounded-full cursor-pointer hover:bg-primary/90 shadow-md transition-colors">
                <Upload className="h-3 w-3" />
                <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} />
              </label>
            </div>
            
            <div className="text-center md:text-left space-y-1 flex-1">
              <h2 className="text-2xl font-bold">{profile.first_name} {profile.last_name}</h2>
              <p className="text-muted-foreground">{profile.designation}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-2">
                <Badge variant="outline">{profile.department}</Badge>
                <Badge variant="outline">ID: {profile.employee_id}</Badge>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {profileStats.map((item) => (
          <StatCard
            key={item.label}
            label={item.label}
            value={item.value}
            icon={item.icon}
            tone={item.tone}
            onClick={() => setActiveProfileTab(item.tab)}
            className={activeProfileTab === item.tab ? 'ring-2 ring-primary/40' : ''}
          />
        ))}
      </div>

      {!isAdminView && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-hr-lg">Profile Completeness</CardTitle>
            <Badge variant={completenessScore >= 80 ? 'success' : completenessScore >= 50 ? 'warning' : 'danger'}>
              {completenessScore}%
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-2 rounded-full ${completenessScore >= 80 ? 'bg-[#10B981]' : completenessScore >= 50 ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'}`}
                style={{ width: `${completenessScore}%` }}
              />
            </div>
            <p className="text-hr-xs text-muted-foreground mt-2">
              Complete your profile details and documents for faster approvals.
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeProfileTab} onValueChange={setActiveProfileTab} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto whitespace-nowrap">
          <TabsTrigger value="personal" className="shrink-0">Personal Details</TabsTrigger>
          <TabsTrigger value="emergency" className="shrink-0">Emergency Contacts</TabsTrigger>
          <TabsTrigger value="job" className="shrink-0">Job History</TabsTrigger>
          <TabsTrigger value="compensation" className="shrink-0">Compensation History</TabsTrigger>
          <TabsTrigger value="documents" className="shrink-0">Documents</TabsTrigger>
          {!isAdminView && <TabsTrigger value="requests" className="shrink-0">Change Requests</TabsTrigger>}
          <TabsTrigger value="timeline" className="shrink-0">Timeline</TabsTrigger>
        </TabsList>
        
        <TabsContent value="personal">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Personal Information</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(!isEditing)}>
                {isEditing ? 'Cancel' : 'Edit'}
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdate} className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={profile.email || ''} disabled className="bg-muted/50" />
                </div>
                <div className="space-y-2">
                  <Label>Date of Joining</Label>
                  <Input value={profile.date_of_joining?.split('T')[0]} disabled className="bg-muted/50" />
                </div>
                
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input 
                    value={formData.phone || ''} 
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input value={profile.date_of_birth || ''} disabled className="bg-muted/50" />
                </div>

                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input 
                    value={formData.location || ''} 
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    disabled={!isEditing || !isAdminView} 
                    className={(!isEditing || !isAdminView) ? "bg-muted/50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employment Status</Label>
                  <Input 
                    value={formData.employment_status || 'Active'} 
                    onChange={(e) => setFormData({...formData, employment_status: e.target.value})}
                    disabled={!isEditing || !isAdminView} 
                    className={(!isEditing || !isAdminView) ? "bg-muted/50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reporting Manager ID</Label>
                  <Input 
                    value={formData.reporting_manager_id || ''} 
                    onChange={(e) => setFormData({...formData, reporting_manager_id: e.target.value})}
                    disabled={!isEditing || !isAdminView} 
                    className={(!isEditing || !isAdminView) ? "bg-muted/50" : ""}
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label>Address</Label>
                  <textarea 
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    rows={3}
                    value={formData.address || ''}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    disabled={!isEditing}
                  />
                </div>

                {isEditing && (
                  <div className="md:col-span-2 flex justify-end">
                    <Button type="submit">
                      <Save className="w-4 h-4 mr-2" /> Save Changes
                    </Button>
                  </div>
                )}
              </form>

              {!isAdminView && (
                <div className="mt-6 border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <p className="text-hr-sm font-medium">Approval Required Fields</p>
                  </div>
                  <div className="space-y-2">
                    {[
                      { field: 'department', label: 'Department', value: profile.department },
                      { field: 'designation', label: 'Designation', value: profile.designation },
                      { field: 'location', label: 'Location', value: profile.location },
                      { field: 'employment_status', label: 'Employment Status', value: profile.employment_status },
                      { field: 'reporting_manager_id', label: 'Reporting Manager ID', value: profile.reporting_manager_id },
                      { field: 'date_of_birth', label: 'Date of Birth', value: profile.date_of_birth },
                    ].map((item) => (
                      <div key={item.field} className="flex items-center justify-between rounded-md border p-3">
                        <div>
                          <p className="text-hr-sm font-medium">{item.label}</p>
                          <p className="text-hr-xs text-muted-foreground">{item.value || 'Not set'}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openChangeRequestDialog(item.field, 'personal')}>
                          <Send className="h-3.5 w-3.5 mr-2" />
                          Request Change
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="emergency">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Emergency Contacts</CardTitle>
              <Dialog open={showContactDialog} onOpenChange={(open) => {
                  setShowContactDialog(open);
                  if (!open) {
                      setEditingContact(null);
                      setNewContact({ name: '', relationship: '', phone: '', email: '', address: '', is_primary: false });
                  }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="w-4 h-4 mr-2" /> Add Contact</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input value={newContact.name} onChange={(e) => setNewContact({...newContact, name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Input value={newContact.relationship} onChange={(e) => setNewContact({...newContact, relationship: e.target.value})} />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={newContact.phone} onChange={(e) => setNewContact({...newContact, phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})} />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="primary" checked={newContact.is_primary} onCheckedChange={(checked) => setNewContact({...newContact, is_primary: checked})} />
                      <Label htmlFor="primary">Is Primary Contact?</Label>
                    </div>
                    <Button onClick={handleAddContact} className="w-full">{editingContact ? 'Update Contact' : 'Save Contact'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex justify-end">
                <TableExportActions
                  rows={emergencyContacts}
                  columns={emergencyContactExportColumns}
                  fileName="emergency_contacts"
                  title="Emergency Contacts"
                />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Relationship</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emergencyContacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell className="font-medium">
                        {contact.name}
                        {contact.is_primary && <Badge variant="soft" className="ml-2">Primary</Badge>}
                      </TableCell>
                      <TableCell>{contact.relationship}</TableCell>
                      <TableCell>{contact.phone}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEditContact(contact)}>
                              <Edit className="w-4 h-4 text-primary" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(contact.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {emergencyContacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="p-0">
                        <EmptyState
                          compact
                          icon={Contact}
                          title="No emergency contacts"
                          description="Add an emergency contact so we know who to reach in urgent situations."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="job">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Job History</CardTitle>
              <Dialog open={showJobDialog} onOpenChange={(open) => {
                  setShowJobDialog(open);
                  if (!open) {
                      setEditingJob(null);
                      setNewJob({ company_name: '', designation: '', start_date: '', end_date: '', is_current_company: false, location: '', description: '' });
                  }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="w-4 h-4 mr-2" /> Add History</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingJob ? 'Edit Job History' : 'Add Job History'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input value={newJob.company_name} onChange={(e) => setNewJob({...newJob, company_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Input value={newJob.designation} onChange={(e) => setNewJob({...newJob, designation: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date</Label>
                        <Input type="date" value={newJob.start_date} onChange={(e) => setNewJob({...newJob, start_date: e.target.value})} />
                      </div>
                      <div>
                        <Label>End Date</Label>
                        <Input type="date" value={newJob.end_date} onChange={(e) => setNewJob({...newJob, end_date: e.target.value})} disabled={newJob.is_current_company} />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="current" checked={newJob.is_current_company} onCheckedChange={(checked) => setNewJob({...newJob, is_current_company: checked})} />
                      <Label htmlFor="current">Current Company?</Label>
                    </div>
                    <Button onClick={handleAddJob} className="w-full">{editingJob ? 'Update History' : 'Save History'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {jobHistory.map((job) => (
                  <div key={job.id} className="relative pl-8 pb-6 border-l-2 border-border last:border-0 group">
                    <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-primary border-2 border-background" />
                    <div className="space-y-1">
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold text-lg">{job.designation}</h4>
                        <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEditJob(job)}>
                              <Edit className="w-4 h-4 text-primary" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteJob(job.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{job.company_name} | {job.location}</div>
                      <div className="text-xs text-muted-foreground">
                        {job.start_date} - {job.is_current_company ? 'Present' : job.end_date}
                      </div>
                      {job.description && <p className="text-sm mt-2">{job.description}</p>}
                    </div>
                  </div>
                ))}
                {jobHistory.length === 0 && (
                  <EmptyState
                    compact
                    icon={Briefcase}
                    title="No job history"
                    description="Previous employment records will appear here once added."
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="compensation">
            <Card>
                <CardHeader>
                    <CardTitle>Compensation History</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-3 flex justify-end">
                        <TableExportActions
                            rows={salaryHistory}
                            columns={salaryHistoryExportColumns}
                            fileName="salary_history"
                            title="Compensation History"
                        />
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Effective From</TableHead>
                                <TableHead>Effective To</TableHead>
                                <TableHead>Annual CTC</TableHead>
                                <TableHead>Net Salary</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {salaryHistory.map((salary) => (
                                <TableRow key={salary.id}>
                                    <TableCell>{salary.effective_from}</TableCell>
                                    <TableCell>{salary.effective_to}</TableCell>
                                    <TableCell>{salary.ctc_annual?.toLocaleString()}</TableCell>
                                    <TableCell>{salary.net_salary?.toLocaleString()}</TableCell>
                                </TableRow>
                            ))}
                            {salaryHistory.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="p-0">
                                        <EmptyState
                                            compact
                                            icon={Wallet}
                                            title="No compensation history"
                                            description="Salary and CTC revisions will be listed here as they are recorded."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="documents">
          <div className="space-y-4">
            {docChecklist && (
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-hr-lg">Checklist Progress</CardTitle>
                  <Badge variant={docChecklist.progress_percent >= 80 ? 'success' : docChecklist.progress_percent >= 50 ? 'warning' : 'danger'}>
                    {docChecklist.progress_percent}%
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div className="h-2 bg-primary rounded-full" style={{ width: `${docChecklist.progress_percent}%` }} />
                  </div>
                  <p className="text-hr-xs text-muted-foreground mt-2">
                    Mandatory uploaded: {docChecklist.mandatory_done}/{docChecklist.mandatory_total}
                  </p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Documents</CardTitle>
                <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm"><Upload className="w-4 h-4 mr-2" /> Upload Document</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Upload Document</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Document Type</Label>
                        <Select onValueChange={(val) => setNewDoc({...newDoc, type_id: val})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                          <SelectContent>
                            {documentTypes.map(type => (
                              <SelectItem key={type.id} value={type.id.toString()}>{type.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>File</Label>
                        <Input type="file" onChange={(e) => setNewDoc({...newDoc, file: e.target.files[0]})} />
                      </div>
                      <div>
                        <Label>Expiry Date (Optional)</Label>
                        <Input type="date" value={newDoc.expiry_date} onChange={(e) => setNewDoc({...newDoc, expiry_date: e.target.value})} />
                      </div>
                      <div>
                        <Label>Notes (Optional)</Label>
                        <Input value={newDoc.notes} onChange={(e) => setNewDoc({...newDoc, notes: e.target.value})} />
                      </div>
                      <Button onClick={handleUploadDocument} className="w-full">Upload</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <p className="font-medium">{doc.document_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.document_type?.name || doc.document_type || 'Document'} • Uploaded on {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                          {doc.expiry_date && (
                            <p className="text-xs text-muted-foreground">
                              Expiry: {doc.expiry_date} {doc.days_to_expiry !== null && doc.days_to_expiry !== undefined ? `(${doc.days_to_expiry} days)` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Badge variant={
                          doc.expiry_state === 'expired' ? 'danger' :
                          doc.expiry_state === 'expiring_soon' ? 'warning' : 'outline'
                        }>
                          {(doc.expiry_state || 'none').replace('_', ' ')}
                        </Badge>
                        <Badge variant={
                          (doc.verification_status || '').toLowerCase() === 'verified' ? 'success' :
                          (doc.verification_status || '').toLowerCase() === 'rejected' ? 'danger' : 'warning'
                        }>
                          {(doc.verification_status || (doc.verified ? 'verified' : 'pending')).toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {documents.length === 0 && (
                    <EmptyState
                      compact
                      icon={FileText}
                      title="No documents uploaded"
                      description="Upload identity, compliance and onboarding documents to keep this profile complete."
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {isAdminView && expiryAlerts.length > 0 && (
              <Card className="border-[hsl(var(--chart-4))]/20 bg-[hsl(var(--chart-4))]/10">
                <CardHeader>
                  <CardTitle className="text-hr-lg">Expiry Alerts (30 days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {expiryAlerts.map((alert) => (
                      <div key={alert.document_id} className="p-3 border rounded-md bg-background flex items-center justify-between">
                        <div>
                          <p className="text-hr-sm font-medium">{alert.document_name}</p>
                          <p className="text-hr-xs text-muted-foreground">{alert.document_type} • {alert.expiry_date}</p>
                        </div>
                        <Badge variant={alert.expiry_state === 'expired' ? 'danger' : 'warning'}>
                          {alert.days_to_expiry} days
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {!isAdminView && (
          <TabsContent value="requests">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Profile Change Requests</CardTitle>
                <Badge variant="outline">{profileChangeRequests.length} total</Badge>
              </CardHeader>
              <CardContent>
                <div className="mb-3 flex justify-end">
                  <TableExportActions
                    rows={profileChangeRequests}
                    columns={profileRequestExportColumns}
                    fileName="profile_change_requests"
                    title="Profile Change Requests"
                  />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Requested Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested On</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profileChangeRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">{request.field_name}</TableCell>
                        <TableCell>{request.current_value || '-'}</TableCell>
                        <TableCell>{request.requested_value}</TableCell>
                        <TableCell>
                          <Badge variant={getRequestBadgeVariant(request.status)}>
                            {String(request.status || '').toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(request.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    {profileChangeRequests.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="p-0">
                          <EmptyState
                            compact
                            icon={GitPullRequest}
                            title="No change requests"
                            description="Requests to update approval-protected fields will appear here for tracking."
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="timeline">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-primary" />
                Profile Timeline
              </CardTitle>
              <Badge variant="outline">{timelineEvents.length} events</Badge>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {timelineEvents.map((event) => (
                  <div key={event.id} className="relative pl-5 border-l border-border pb-4 last:pb-0">
                    <div className="absolute left-[-5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                    <p className="text-hr-sm font-medium">{event.title}</p>
                    <p className="text-hr-xs text-muted-foreground">{event.detail}</p>
                    <p className="text-hr-xs text-muted-foreground mt-1">{new Date(event.timestamp).toLocaleString()}</p>
                  </div>
                ))}
                {timelineEvents.length === 0 && (
                  <EmptyState
                    compact
                    icon={History}
                    title="No timeline events"
                    description="Profile milestones and activity will build up here over time."
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {!isAdminView && (
        <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Change Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Field</Label>
                <Input value={changeDraft.field_name} disabled className="bg-muted/50" />
              </div>
              <div>
                <Label>Requested Value</Label>
                <Input
                  value={changeDraft.requested_value}
                  onChange={(e) => setChangeDraft({ ...changeDraft, requested_value: e.target.value })}
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Input
                  value={changeDraft.reason}
                  onChange={(e) => setChangeDraft({ ...changeDraft, reason: e.target.value })}
                />
              </div>
              <Button onClick={handleSubmitChangeRequest} className="w-full">
                Submit for Approval
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProfileSection;
